import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Fade,
  Chip,
  Divider,
} from '@mui/material';
import {
  CheckCircle,
  CalendarToday,
  Scale,
} from '@mui/icons-material';
import { DESIGN_TOKENS } from '../../../design-system/tokens/colors';

export interface WeaningData {
  data: {
    message: string;
    content_type: string;
    status: string;
    name: string;
    breed_code: string;
    gender: string;
    weight_kg: number;
    weaning_date: string;
    handling_date: string;
    missing_fields: string[];
    cattle_tag: string;
    operation_code: string;
    summary: string;
    age_months: number;
  };
  resumo: string;
  url: string;
}

export interface WeaningFeedbackProps {
  weaningData: WeaningData;
  onNavigate?: (url: string) => void;
}

const WeaningFeedback: React.FC<WeaningFeedbackProps> = ({
  weaningData,
  onNavigate,
}) => {
  const { data, resumo, url } = weaningData;

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('pt-BR');
    } catch {
      return dateString;
    }
  };



  const handleNavigate = () => {
    if (onNavigate && url) {
      onNavigate(url);
    }
  };

  return (
    <Fade in timeout={800}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          padding: 3,
          background: `linear-gradient(135deg, ${DESIGN_TOKENS.colors.primary[50]} 0%, ${DESIGN_TOKENS.colors.background.default} 100%)`,
        }}
      >
        {/* Ícone principal animado */}
        <Box
          sx={{
            position: 'relative',
            marginBottom: 3,
            animation: 'pulse 2s infinite',
            '@keyframes pulse': {
              '0%': {
                transform: 'scale(1)',
                boxShadow: `0 0 0 0 ${DESIGN_TOKENS.colors.primary[200]}40`,
              },
              '70%': {
                transform: 'scale(1.05)',
                boxShadow: `0 0 0 20px ${DESIGN_TOKENS.colors.primary[200]}00`,
              },
              '100%': {
                transform: 'scale(1)',
                boxShadow: `0 0 0 0 ${DESIGN_TOKENS.colors.primary[200]}00`,
              },
            },
          }}
        >
          <Avatar
            sx={{
              width: 80,
              height: 80,
              backgroundColor: DESIGN_TOKENS.colors.primary[100],
              border: `3px solid ${DESIGN_TOKENS.colors.primary[200]}`,
              boxShadow: `0 8px 32px ${DESIGN_TOKENS.colors.primary[200]}40`,
            }}
          >
            <Box
              component="img"
              src="/vaca.svg"
              alt="Vaca"
              sx={{
                width: 40,
                height: 40,
                color: DESIGN_TOKENS.colors.primary[700],
              }}
            />
          </Avatar>
          
          {/* Ícone de sucesso sobreposto */}
          <Avatar
            sx={{
              width: 32,
              height: 32,
              backgroundColor: DESIGN_TOKENS.colors.primary[600],
              position: 'absolute',
              bottom: -4,
              right: -4,
              border: `2px solid ${DESIGN_TOKENS.colors.background.paper}`,
            }}
          >
            <CheckCircle
              sx={{
                fontSize: 20,
                color: DESIGN_TOKENS.colors.background.paper,
              }}
            />
          </Avatar>
        </Box>

        {/* Card principal */}
        <Card
          sx={{
            maxWidth: 500,
            width: '100%',
            borderRadius: 3,
            boxShadow: `0 12px 40px ${DESIGN_TOKENS.colors.primary[200]}30`,
            border: `1px solid ${DESIGN_TOKENS.colors.primary[100]}`,
            overflow: 'visible',
          }}
        >
          <CardContent sx={{ padding: 4, textAlign: 'center' }}>
            {/* Título principal */}
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: DESIGN_TOKENS.colors.text.primary,
                marginBottom: 1,
              }}
            >
              Desmama Registrada!
            </Typography>

            {/* Subtítulo */}
            <Typography
              variant="body1"
              sx={{
                color: DESIGN_TOKENS.colors.text.secondary,
                marginBottom: 3,
              }}
            >
              {data.summary}
            </Typography>

            {/* Informações do animal */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                marginBottom: 3,
                padding: 3,
                backgroundColor: DESIGN_TOKENS.colors.primary[50],
                borderRadius: 2,
                border: `1px solid ${DESIGN_TOKENS.colors.primary[100]}`,
              }}
            >
              {/* Nome e brinco do animal */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                <img 
                  src="/vaca.svg" 
                  alt="Animal" 
                  style={{ 
                    width: 24, 
                    height: 24, 
                    filter: `brightness(0) saturate(100%) invert(20%) sepia(77%) saturate(1200%) hue-rotate(180deg) brightness(95%) contrast(101%)` 
                  }} 
                />
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: DESIGN_TOKENS.colors.primary[700],
                  }}
                >
                  {data.name}
                </Typography>
              </Box>

              {/* Brinco */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: DESIGN_TOKENS.colors.text.secondary,
                    fontWeight: 500,
                  }}
                >
                  Brinco:
                </Typography>
                <Chip
                  label={data.cattle_tag}
                  size="small"
                  sx={{
                    backgroundColor: DESIGN_TOKENS.colors.secondary[100],
                    color: DESIGN_TOKENS.colors.secondary[700],
                    fontWeight: 600,
                  }}
                />
              </Box>

              {/* Raça */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: DESIGN_TOKENS.colors.text.secondary,
                    fontWeight: 500,
                  }}
                >
                  Raça:
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: DESIGN_TOKENS.colors.primary[700],
                    fontWeight: 600,
                  }}
                >
                  {data.breed_code}
                </Typography>
              </Box>

              <Divider sx={{ my: 1 }} />

              {/* Data da desmama */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                <CalendarToday sx={{ fontSize: 16, color: DESIGN_TOKENS.colors.primary[600] }} />
                <Typography
                  variant="body2"
                  sx={{
                    color: DESIGN_TOKENS.colors.text.secondary,
                    fontWeight: 500,
                  }}
                >
                  Data da Desmama:
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: DESIGN_TOKENS.colors.primary[700],
                    fontWeight: 600,
                  }}
                >
                  {formatDate(data.weaning_date)}
                </Typography>
              </Box>

              {/* Status */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                <Chip
                  icon={<CheckCircle sx={{ fontSize: 16 }} />}
                  label={data.status}
                  sx={{
                    backgroundColor: '#eff6ff',
                    color: '#1d4ed8',
                    fontWeight: 600,
                    '& .MuiChip-icon': {
                      color: '#1d4ed8',
                    },
                  }}
                />
              </Box>


            </Box>

            {/* Mensagem de sucesso */}
            <Typography
              variant="body2"
              sx={{
                color: DESIGN_TOKENS.colors.text.tertiary,
                fontStyle: 'italic',
              }}
            >
              {data.message}
            </Typography>
          </CardContent>
        </Card>
      </Box>
    </Fade>
  );
};

export default WeaningFeedback;